import { useState, useEffect, useCallback } from 'react';
import { mediaPlanningApi } from '../services/api/mediaPlanningApi';

interface OverviewData {
    /** null means the avails summary has no computed value yet — distinct from a genuine zero. */
    totalImpressions: number | null;
    brandSafeUrls: number;
    matchRate: number;
    lastUpdated: string | null;
}

interface DimensionData {
    dimension: string;
    title: string;
    items: Array<{ value: string; rawValue?: string; impressions: number; uniqueUrls?: number; percentage: number }>;
}

export function useMediaPlanningData(promptId: string | null, filters: Record<string, string>) {
    const [overview, setOverview] = useState<OverviewData | null>(null);
    const [dimensions, setDimensions] = useState<DimensionData[]>([]);
    const [insights, setInsights] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!promptId) {
            setOverview(null);
            setDimensions([]);
            setInsights(null);
            setError(null);
            return;
        }
        loadData(promptId, filters, { setOverview, setDimensions, setInsights, setLoading, setError });
    }, [promptId, filters]);

    return { overview, dimensions, insights, loading, error };
}

interface Setters {
    setOverview: (v: OverviewData | null) => void;
    setDimensions: (v: DimensionData[]) => void;
    setInsights: (v: any) => void;
    setLoading: (v: boolean) => void;
    setError: (v: string | null) => void;
}

async function loadData(promptId: string, filters: Record<string, string>, s: Setters) {
    s.setLoading(true);
    s.setError(null);
    try {
        const [overviewData, dimensionsData, insightsData] = await Promise.all([
            mediaPlanningApi.getOverview(promptId),
            mediaPlanningApi.getDimensions(promptId, filters),
            mediaPlanningApi.getInsights(promptId, filters),
        ]);
        s.setOverview(overviewData);
        s.setDimensions(dimensionsData);
        s.setInsights(insightsData);
    } catch (err: any) {
        s.setError(err.message || 'Failed to load media planning data');
    } finally {
        s.setLoading(false);
    }
}

export function useComparisonData(comparisonIds: string[]) {
    const [comparisonData, setComparisonData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (comparisonIds.length < 2) {
            setComparisonData([]);
            return;
        }
        loadComparison(comparisonIds, setComparisonData, setLoading);
    }, [comparisonIds]);

    return { comparisonData, comparisonLoading: loading };
}

async function loadComparison(
    ids: string[],
    setData: (v: any[]) => void,
    setLoading: (v: boolean) => void,
) {
    setLoading(true);
    try {
        const data = await mediaPlanningApi.getComparison(ids);
        setData(data);
    } catch (err) {
        console.error('Failed to load comparison:', err);
    } finally {
        setLoading(false);
    }
}

export function useRecommendations(
    promptId: string | null,
    overview: any,
    filters: Record<string, string>,
    dimensions: any[],
) {
    const [recommendations, setRecommendations] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const generate = useCallback(async () => {
        if (!promptId || !overview) return;
        setLoading(true);
        try {
            const context = buildRecommendationContext(promptId, filters, overview, dimensions);
            const recs = await mediaPlanningApi.getRecommendations(context);
            setRecommendations(recs);
        } catch (err) {
            console.error('Failed to generate recommendations:', err);
        } finally {
            setLoading(false);
        }
    }, [promptId, overview, filters, dimensions]);

    const dismiss = useCallback((id: string) => {
        setRecommendations((prev) =>
            prev.map((r) => (r.id === id ? { ...r, dismissed: true } : r)),
        );
    }, []);

    return { recommendations, recommendationsLoading: loading, generate, dismiss };
}

export interface HistoricalTrend {
    date: string;
    totalAvails: number;
    brandSafeUrls: number;
}

export function useHistoricalTrends(promptId: string | null, days: number = 30) {
    const [trends, setTrends] = useState<HistoricalTrend[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!promptId) {
            setTrends([]);
            return;
        }
        loadTrends(promptId, days, setTrends, setLoading);
    }, [promptId, days]);

    return { trends, trendsLoading: loading };
}

async function loadTrends(
    promptId: string,
    days: number,
    setData: (v: HistoricalTrend[]) => void,
    setLoading: (v: boolean) => void,
) {
    setLoading(true);
    try {
        const data = await mediaPlanningApi.getTrends(promptId, days);
        setData(data);
    } catch (err) {
        console.error('Failed to load historical trends:', err);
    } finally {
        setLoading(false);
    }
}

function buildRecommendationContext(
    promptId: string,
    filters: Record<string, string>,
    overview: any,
    dimensions: any[],
) {
    return {
        promptId,
        promptName: 'Selected Blueprint',
        filters,
        overview,
        dimensions: Object.fromEntries(
            dimensions.map((d: any) => [d.dimension, d.items]),
        ),
    };
}
